import { useState } from "react";
import { KeyRound } from "lucide-react";
import { useApiMutation } from "@/hooks/use-api";
import { useAuthStore } from "@/store/auth.store";
import { useUiStore } from "@/store/ui.store";
import { Button, Card, CardBody, CardHeader, Field, Input, Modal } from "@/components/ui";
import { freshOnOpen } from "@/lib/fresh-on-open";

const MIN_LENGTH = 10;

/**
 * Changing a password signs the account out everywhere on the server, so the
 * form ends by signing out here too rather than leaving a session that is about
 * to stop working.
 */
function useChangePassword(onDone?: () => void) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const logout = useAuthStore((s) => s.logout);
  const toast = useUiStore((s) => s.toast);

  const change = useApiMutation<{ currentPassword: string; newPassword: string }>("/auth/change-password", {
    onSuccess: () => {
      onDone?.();
      toast({ title: "Password changed", description: "Sign in again with your new password.", variant: "success" });
      void logout();
    },
  });

  const error =
    next && next.length < MIN_LENGTH
      ? `At least ${MIN_LENGTH} characters`
      : confirm && next !== confirm
        ? "The two new passwords don't match"
        : next && next === current
          ? "Choose a password different from the current one"
          : undefined;
  const ready = Boolean(current && next && confirm) && !error;
  const submit = () => ready && change.mutate({ currentPassword: current, newPassword: next });

  const fields = (
    <form
      className="space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <Field label="Current password" required>
        <Input type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} autoFocus />
      </Field>
      <Field label="New password" required hint={`At least ${MIN_LENGTH} characters`}>
        <Input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} />
      </Field>
      <Field label="Confirm new password" required error={error}>
        <Input type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
      </Field>
      <button type="submit" hidden />
    </form>
  );

  return { fields, ready, submit, pending: change.isPending };
}

export const ChangePasswordModal = freshOnOpen(ChangePasswordModalForm);

function ChangePasswordModalForm({ open, onClose }: { open: boolean; onClose: () => void }) {
  const form = useChangePassword(onClose);
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Change password"
      description="You'll be signed out on every device and asked to sign in again."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={!form.ready} loading={form.pending} onClick={form.submit}>
            Change password
          </Button>
        </>
      }
    >
      {form.fields}
    </Modal>
  );
}

/** Shown instead of the app until someone replaces a one-time password. */
export function ForcedPasswordChange() {
  const form = useChangePassword();
  const profile = useAuthStore((s) => s.profile);
  const logout = useAuthStore((s) => s.logout);

  return (
    <div className="flex min-h-full items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader
          title="Choose your password"
          description={`Welcome${profile ? `, ${profile.fullName}` : ""}. You signed in with a one-time password; set your own to continue.`}
        />
        <CardBody>
          {form.fields}
          <div className="mt-4 flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={() => void logout()}>
              Sign out
            </Button>
            <Button
              disabled={!form.ready}
              loading={form.pending}
              onClick={form.submit}
              icon={<KeyRound className="h-3.5 w-3.5" />}
            >
              Set password
            </Button>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
